import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import NewsletterSignUp from '../../common/Newsletter-sign';
import ImageSlant from '../../common/image-slant';
import heroImage from '../../../assets/christmas25/festive.webp';
import teaImage from '../../../assets/christmas25/tea2.webp';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const Page = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const EventHero = styled.div`
  width: 100%;
  height: 420px;
  background-image: ${props => `linear-gradient(to bottom, rgba(0,0,0,0.25), rgba(0,0,0,0.65)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: relative;

  @media (max-width: 1024px) {
    height: 420px;
  }

  @media (max-width: 768px) {
    height: 340px;
  }
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1400px;
  padding: 3rem 2rem;
  color: white;
`;

const Badge = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  font-weight: 700;
  margin: 0.25rem 0 0.5rem;
  line-height: 1.2;
  text-shadow: 0 2px 6px rgba(0,0,0,0.35);
`;

const Strapline = styled.p`
  font-size: 1.25rem;
  margin: 0;
  opacity: 0.95;
`;

const Content = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 1.5rem auto 0;
  padding: 0 2rem 3rem;
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 2.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    margin-top: 1rem;
  }
`;

const Card = styled.section.attrs({ className: 'bf-surface' })`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  z-index: 1;
  position: relative;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 80px;
    height: 3px;
    background: ${props => props.theme.colors.accent};
  }
`;

const Body = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #333;

  p { margin: 0 0 1rem; }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div.attrs({ className: 'bf-surface' })`
  background: ${props => props.theme.colors.offWhite};
  padding: 1rem 1.25rem;
  border-radius: 8px;
`;

const InfoLabel = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.25rem;
`;

const CTAButton = styled.a.attrs({ className: 'cta-button' })`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.9rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 700;
  margin-top: 1.25rem;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.95;
    transform: translateY(-2px);
  }
`;

const MiceTrail = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';
  const meta = {
    title: 'Mice Trail | Christmas 2025 at Belvoir Castle',
    description: 'Follow the delightful Mice Trail through Belvoir Castle’s grand rooms. A charming, family-friendly trail included with your Entry Ticket (and Search for Santa).',
    canonical: 'https://www.belvoircastle.com/christmas/mice-trail'
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Mice Trail at Belvoir Castle',
    description: meta.description,
    url: meta.canonical,
    image: [heroImage]
  };

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content={heroImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Mice Trail at Belvoir Castle" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={heroImage} />
        <meta name="twitter:site" content="@belvoircastle" />
        <meta name="twitter:url" content={meta.canonical} />
        <meta name="twitter:image:alt" content="Mice Trail at Belvoir Castle" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <EventHero image={heroImage}>
        <HeroContent>
          <Badge>Included with Entry</Badge>
          <Title>Mice Trail</Title>
          <Strapline>A charming family trail throughout the Castle</Strapline>
        </HeroContent>
      </EventHero>

      <Content>
        <Card>
          <SectionTitle>About the Trail</SectionTitle>
          <Body>
            <p>New for 2025, our beloved Three Mice have arrived! Follow the delightful Mice Trail through Belvoir Castle’s grand rooms to uncover tiny surprises around every corner — a charming, interactive adventure designed especially for children to explore and enjoy.</p>
            <p>The Mice Trail is included with your Entry Ticket and is also available to guests on the Search for Santa experience. Pick up your trail sheet on arrival and see how many mice you can spot!</p>
          </Body>

          <CTAButton href="/booking-journey?locationId=978&pageId=278ef0c8-a65b-4e31-9726-305ae06a5a14">Start Your Booking</CTAButton>
        </Card>

        <Card>
          <SectionTitle>Key Information</SectionTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Dates</InfoLabel>
              <div>29 November – 31 December 2025</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Included With</InfoLabel>
              <div>Entry Ticket; Search for Santa</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Location</InfoLabel>
              <div>Belvoir Castle</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Price</InfoLabel>
              <div>Included with Entry</div>
            </InfoItem>
            <InfoItem>
      
              <CTAButton href="/booking-journey?locationId=978&pageId=278ef0c8-a65b-4e31-9726-305ae06a5a14">Book Now</CTAButton>
            </InfoItem>
          </InfoGrid>
        </Card>
      </Content>

      <ImageSlant 
        image={teaImage}
        title="Festive Afternoon Tea"
        description="Make your day even more memorable with indulgent winter treats in our elegant tearooms."
        buttonText="Book Festive Tea"
        buttonAction={() => window.location.assign('/christmas/festive-afternoon-tea')}
        imagePosition="left"
        darkMode={isBlackFriday}
      />

      <div style={{ margin: '3rem 0' }}>
        <NewsletterSignUp tag="Christmas 2025" />
      </div>
    </Page>
  );
};

export default MiceTrail;

